import type { ReactNode } from "react";

// IconTile — the 48px rounded square an icon sits in when it leads a row of text.
//
// Extracted in QA-94 because a third surface wanted it. The grid's proposal footer had it inline
// and the confirm card had grown a DIFFERENT glyph — a 38px circle with a `--cell-partly-bd` ring
// — so the two screens either side of a single click showed the same calendar two ways, one of them
// borrowing a token that belongs to a grid CELL.
//
// `--space-7` (48px), NOT `h-12`: at this app's 15px root `h-12` resolves to 45px. `ActionCard`
// still carries its own `h-12` copy of this tile and is therefore still 45 — left alone
// deliberately, since it ships on every card in the Sessions view, but that is the next consumer.

export interface IconTileProps {
    children: ReactNode;
    /**
     * `neutral` — `--canvas` under `--ink-soft`, inside a `--hairline` border. The default, and
     * the only tone the app uses today: the tile is a container for an icon, not a status.
     * Anything that needs to signal severity does it with the words beside it, or with a panel
     * around them.
     *
     * The border is QA-106, applied to every tile in the app — icon and counter alike. A filled
     * square with no edge reads as a smudge on a warm ground; the hairline gives it the same
     * definition every other enclosure in the DS has.
     *
     * The fill moved from `--muted` to `--canvas` (2026-08-31). `--muted` is the token for an area
     * that RECEDES — hover feedback, disabled fill — and at #DED9D9 it was the darkest thing in a
     * row of text, pulling the eye to a container rather than to the words beside it. `--canvas`
     * is the page's own ground, so the tile now reads as a hole in the card rather than a chip on
     * it, and the hairline is what keeps its edge.
     */
    tone?: "neutral";
    className?: string;
}

export function IconTile({ children, tone = "neutral", className = "" }: IconTileProps) {
    return (
        <div
            data-icon-tile
            data-tone={tone}
            aria-hidden
            className={`flex h-[var(--space-48)] w-[var(--space-48)] shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-border bg-canvas text-secondary ${className}`}
        >
            {children}
        </div>
    );
}
